import { useEffect, useRef, useState } from "react";

import { isFinishedJob, isOpenJob } from "~/helpers/backgroundJobs/backgroundJobQueue";

interface JobStatusSnapshot {
	jobId: string;
	status: string;
}

const useFinishedJobTransitions = <T extends JobStatusSnapshot>(
	jobs: readonly T[] | undefined,
	enabled: boolean
): readonly T[] => {
	const seenStatus = useRef(new Map<string, string>());
	const primed = useRef(false);
	const [finished, setFinished] = useState<readonly T[]>([]);

	useEffect(() => {
		if (!enabled || !jobs) return;
		if (!primed.current) {
			for (const job of jobs) seenStatus.current.set(job.jobId, job.status);
			primed.current = true;
			return;
		}
		const justFinished = jobs.filter((job) => {
			const previous = seenStatus.current.get(job.jobId);
			const wasOpen = previous === undefined || isOpenJob({ status: previous });
			return wasOpen && isFinishedJob(job);
		});
		for (const job of jobs) seenStatus.current.set(job.jobId, job.status);
		if (justFinished.length > 0) setFinished(justFinished);
	}, [enabled, jobs]);

	return finished;
};

export default useFinishedJobTransitions;
